import { Loading } from '@arcadiapower/shrike';
import { merge } from 'lodash';
import { useEffect, useState } from 'react';
import { LegalMachine } from '~/machines/legal-completion';
import { ContextSendMatch } from '~/machines/utils/types';
import { LDClient, LDFlags } from '~/providers';
import { fetchWaitlistOptions } from '~/services/';
import { FunnelTrack } from '~/typings/graphql';
import { DeepPartial } from '~/typings/utils';
import { trackError } from '~/utils/tracking';
import { DataEntryReview } from './data-entry-review.component';
import { VnmTerms, VnmTermsProps } from './vnm-terms.component';

type VnmTermsContainerProps = {
  micro: ContextSendMatch<LegalMachine>;
  overrideCopy?: VnmTermsProps['overrideCopy'];
  overrideProps?: DeepPartial<VnmTermsProps>;
};

export const VnmTermsContainer = ({
  micro: { context, matches, send },
  overrideCopy,
  overrideProps,
}: VnmTermsContainerProps) => {
  const [isUtilityScraperEligible, setisUtilityScraperEligible] = useState<any>(false);
  const [isFetchWaitListOptionsResolved, setIsFetchWaitListOptionsResolved] = useState<any>(false);

  const baseOverrideCopy = overrideCopy || (() => ({}));

  const finalOverrideCopy = LDClient.variation(LDFlags.enableAttestationFields)
    ? context => ({
        ...baseOverrideCopy(context),
        isLmi: {
          primary: 'Yes, I qualify as LMI OVERRIRDE',
          secondary:
            "If you choose this option, fill out the 'LMI Criteria' section that appears below and then click the 'Looks good to me' button. You will be taken to a form to self-attest your income status.",
        },
        maybeLmi: {
          primary: "I don't know if I qualify as LMI",
          secondary:
            "If you aren't sure, that's ok. Fill out the 'LMI Criteria' section that appears below and then click the 'Looks good to me' button. You will be taken to a form to self-attest your income status. Go ahead and sign it. We will review your income level and check whether you qualify as LMI.",
        },
        notLmi: {
          primary: 'No, I do not qualify as LMI',
          secondary: "You're still guaranteed to save with community solar!",
        },
      })
    : baseOverrideCopy;

  const props: VnmTermsProps = {
    context: {
      billingSelection: context.billingSelection,
      funnelPacket: context.funnelPacket,
      contracts: context.vnmSolarProjectContracts,
      email: context.funnelPacket.user.email,
      firstName: context.funnelPacket.user.firstName,
      lastName: context.funnelPacket.user.lastName,
      lmiStatus: context.lmiStatus!,
      paymentMethod: context.paymentMethod,
      phoneNumber: context.funnelPacket.user.phoneNumber,
      promoCode: context.funnelPacket.user.promoCode,
      solarSavingsRate: context.solarSavingsRate,
      utility: context.funnelPacket.utility,
      utilityAccountId: context.funnelPacket.utilityAccount.id,
      isUtilityScraperEligible,
      forceWaitlist: context.forceWaitlist,
      funnelId: context.funnelId,
    },
    flags: {
      showReviewBlock:
        context.showLmi && context.funnelPacket.utility.funnelTrack !== FunnelTrack.Waitlist,
      showSelfAttestationForm: !!LDClient.variation(LDFlags.enableAttestationFields),
      showPersonalizedContractPreview: LDClient.variation(LDFlags.showPersonalizedContractPreview),
    },
    error: context.errors.review,
    loading: matches('review.fetching'),
    next: data => {
      send({ type: 'SUBMIT_TERMS', data });
    },
    overrideCopy: finalOverrideCopy,
    pending: matches('review.submitting'),
  };

  const utilityScraperEligibleOptions = async () => {
    try {
      const response = await fetchWaitlistOptions({
        utilityId: context.funnelPacket.utility.id,
      });
      setisUtilityScraperEligible(response?.utilityScrapeEligible);
      setIsFetchWaitListOptionsResolved(true);
    } catch (error) {
      trackError(new Error('FetchWaitListOptions fetching scraper eligible data'), {
        extras: { error },
      });
    }
  };

  const isEnergywell = context.isEnergyWell;
  const { isPowerSetter } = context;

  useEffect(() => {
    utilityScraperEligibleOptions();
  }, []);

  let content;
  if (isFetchWaitListOptionsResolved) {
    if (isEnergywell || isPowerSetter) {
      content = <DataEntryReview {...merge(props, overrideProps)} />;
    } else {
      content = <VnmTerms {...merge(props, overrideProps)} />;
    }
  } else {
    content = <Loading />;
  }

  return content;
};
